/**
 * Admin › Marketing Signups
 * Lists everyone who has dropped their email on `/family-and-memorials`
 * (or any future marketing page). Filter + search + one-click CSV
 * export. Rows are safe to un-register (best-effort — send a friendly
 * unsubscribe email out of band).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, API } from "@/lib/api";
import { Download, Search, Trash2, Loader2, Mail } from "lucide-react";

export default function AdminMarketingSignups() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (source) params.set("source", source);
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", "500");
      const { data } = await http.get(`/admin/marketing/signups?${params.toString()}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load signups.");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [source]);

  const remove = async (id, email) => {
    if (!window.confirm(`Delete ${email}? This cannot be undone.`)) return;
    try {
      await http.delete(`/admin/marketing/signups/${id}`);
      toast.success("Removed.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove."); }
  };

  const downloadCsv = async () => {
    // Streaming endpoint — open in a new tab so the browser saves the file directly.
    const params = new URLSearchParams();
    if (source) params.set("source", source);
    const url = `${API}/admin/marketing/signups/export${params.toString() ? `?${params.toString()}` : ""}`;
    // The endpoint is auth-protected — reuse the axios session by fetching then blob-downloading.
    try {
      const res = await http.get(url.replace(API, ""), { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `marketing_signups_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error("Failed to export CSV.");
    }
  };

  const uniqueSources = Array.from(new Set(rows.flatMap((r) => r.sources || []))).sort();

  return (
    <div data-testid="admin-marketing-signups">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline mb-2">MARKETING · EMAIL SIGNUPS</div>
          <h2 className="font-display text-2xl">Family & Memorials list</h2>
          <p className="text-sm opacity-70 mt-1">
            {total} address{total === 1 ? "" : "es"} captured across the marketing surfaces. Export anytime for your email tool.
          </p>
        </div>
        <button onClick={downloadCsv} className="wofy-btn-primary text-sm" data-testid="marketing-export-csv">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 bg-white" style={{ borderColor: "var(--wofy-border)" }}>
          <Search className="w-4 h-4 opacity-60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search email…"
            className="bg-transparent outline-none text-sm w-56"
            data-testid="marketing-search"
          />
        </div>
        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="rounded-full border px-3 py-1.5 text-sm bg-white"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="marketing-source-filter"
        >
          <option value="">All sources</option>
          {uniqueSources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {busy && <div className="text-sm opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>}

      {!busy && rows.length === 0 && (
        <div className="rounded-xl border-2 border-dashed p-10 text-center text-sm opacity-70" style={{ borderColor: "var(--wofy-border)" }} data-testid="marketing-empty">
          <Mail className="w-6 h-6 mx-auto mb-2 opacity-60" />
          No signups yet. Once visitors submit the form on the marketing page, they'll appear here.
        </div>
      )}

      {rows.length > 0 && (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--wofy-border)" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "rgba(0,0,0,0.03)" }}>
              <tr className="text-left">
                <th className="px-3 py-2 font-mono text-[10px] uppercase opacity-70">Email</th>
                <th className="px-3 py-2 font-mono text-[10px] uppercase opacity-70">Name</th>
                <th className="px-3 py-2 font-mono text-[10px] uppercase opacity-70">Sources</th>
                <th className="px-3 py-2 font-mono text-[10px] uppercase opacity-70">Signed up</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" style={{ borderColor: "var(--wofy-border)" }} data-testid={`marketing-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.email}</td>
                  <td className="px-3 py-2">{r.name || <span className="opacity-40">—</span>}</td>
                  <td className="px-3 py-2 text-xs">{(r.sources || []).join(", ")}</td>
                  <td className="px-3 py-2 text-xs opacity-70">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => remove(r.id, r.email)} className="opacity-40 hover:opacity-100" aria-label={`Remove ${r.email}`} data-testid={`marketing-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4" style={{ color: "#B23A3A" }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
